/**
 * Readiness — API client.
 * Mirrors src/lib/supabase/readiness.ts function signatures exactly.
 */
import { apiGet, apiPost, apiPatch, apiFetch } from './client';
import type { ReadinessSession, ReleaseRecord, SendRecord, MoldReadinessRow, UnmappedItem } from '@/types/readiness';
import { format } from 'date-fns';

// ─── Sessions ────────────────────────────────────────────────────────────────

export async function fetchReadinessSession(_orgId: string | null): Promise<ReadinessSession | null> {
  return apiGet<ReadinessSession | null>('/api/readiness/session');
}

export async function upsertReadinessSession(
  session: ReadinessSession,
  _orgId: string,
): Promise<void> {
  await apiFetch<void>('/api/readiness/session', {
    method: 'PUT',
    body: JSON.stringify(session),
  });
}

// ─── Releases ────────────────────────────────────────────────────────────────

export async function fetchReleases(sessionId: string): Promise<ReleaseRecord[]> {
  return apiGet<ReleaseRecord[]>(`/api/readiness/releases?session_id=${sessionId}`);
}

export async function createRelease(input: {
  id: string;
  readiness_session_id: string;
  released_for_date: string;
  released_at: string;
  released_by: string;
  status: ReleaseRecord['status'];
}): Promise<void> {
  await apiPost<void>('/api/readiness/releases', input);
}

export async function updateRelease(
  id: string,
  data: { status?: ReleaseRecord['status']; released_at?: string; released_by?: string },
): Promise<void> {
  await apiPatch<void>(`/api/readiness/releases/${id}`, data);
}

// ─── Send Records ─────────────────────────────────────────────────────────────

export async function fetchSendRecords(sessionId: string): Promise<SendRecord[]> {
  return apiGet<SendRecord[]>(`/api/readiness/send-records?session_id=${sessionId}`);
}

export async function createSendRecord(input: {
  id: string;
  readiness_session_id: string;
  sent_for_date: string;
  sent_at: string;
  sent_by: string;
  plan_version: number;
  ready_count: number;
  at_risk_count: number;
  not_ready_count: number;
  send_anyway_reason?: string;
  snapshot: SendRecord['snapshot'];
}): Promise<void> {
  await apiPost<void>('/api/readiness/send-records', input);
}

// ─── CSV Export (client-side) ─────────────────────────────────────────────────

function esc(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportReadinessCSV(
  moldRows: MoldReadinessRow[],
  unmappedItems: UnmappedItem[],
  session: ReadinessSession,
): void {
  const lines: string[] = [];

  // ── Section 1: Plan info ──
  lines.push('Plan Info');
  lines.push(`Version,${session.version}`);
  lines.push(`Generated at,${format(new Date(), 'yyyy-MM-dd HH:mm')}`);
  lines.push(`Updated at,${format(new Date(session.updated_at), 'yyyy-MM-dd HH:mm')}`);
  lines.push('');

  // ── Section 2: Per-date summary ──
  lines.push('Date Summary');
  lines.push('Date,Total Molds,Ready,At Risk,Not Ready');
  const dateGroups = new Map<string, MoldReadinessRow[]>();
  moldRows.forEach(r => {
    if (!dateGroups.has(r.requested_date)) dateGroups.set(r.requested_date, []);
    dateGroups.get(r.requested_date)!.push(r);
  });
  Array.from(dateGroups.keys()).sort().forEach(date => {
    const rows = dateGroups.get(date)!;
    const ready = rows.filter(r => r.readiness === 'ready').length;
    const atRisk = rows.filter(r => r.readiness === 'at_risk').length;
    const notReady = rows.filter(r => r.readiness === 'not_ready' || r.readiness === 'unknown').length;
    lines.push([esc(date), rows.length, ready, atRisk, notReady].join(','));
  });
  lines.push('');

  // ── Section 3: Mold detail rows ──
  lines.push('Mold Detail');
  lines.push('Date,Mold ID,Mold Name,Location,Operational Status,Readiness,Reasons');
  Array.from(dateGroups.keys()).sort().forEach(date => {
    dateGroups.get(date)!.forEach(row => {
      const reasons = row.reasons.map(r => r.text).join('; ');
      lines.push([
        esc(date), esc(row.mold_display_id), esc(row.mold_name),
        esc(row.location), esc(row.computed_status), esc(row.readiness), esc(reasons),
      ].join(','));
    });
  });
  lines.push('');

  // ── Section 4: Unmapped items ──
  if (unmappedItems.length > 0) {
    lines.push('Unmapped Items (no mold found)');
    lines.push('Part Number,Requested Date');
    unmappedItems.forEach(item => {
      lines.push([esc(item.part_number), esc(item.requested_date)].join(','));
    });
  }

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `readiness_v${session.version}_${format(new Date(), 'yyyyMMdd_HHmm')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
